import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import US_PLACES from "@/lib/usPlaces";

/* A location field that suggests as you type.

   Everywhere else on the site this is a bare text input, so the same city
   arrives as "Indianapolis", "indianapolis, in", "Indy" and "Indianapolis
   Indiana". That is not a tidiness problem: location is one of the five signals
   the matcher weights, and it is matched with an ILIKE, so a spelling nobody
   else used simply does not match anybody.

   Deliberately no geocoding service. Every option costs an API key, a per-call
   bill, a privacy disclosure and a third-party host that the app's CSP would
   have to allow, in exchange for suggesting cities we can just ship. The list
   is bundled: it works offline, inside Meta's in-app browser, and on the first
   keystroke with no network round trip.

   Free text still wins. Somebody in a town that is not on the list types it and
   carries on -- the suggestions are a shortcut, never a gate. */

export default function LocationInput({
  value,
  onChange,
  id,
  placeholder = "City, state, or Remote",
  className = "",
  inputProps = {},
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrap = useRef(null);

  const matches = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (q.length < 2) return [];
    // Prefix hits first: somebody typing "car" wants Carmel before Bel Air.
    const starts = [];
    const contains = [];
    for (const p of US_PLACES) {
      const low = p.toLowerCase();
      if (low.startsWith(q)) starts.push(p);
      else if (low.includes(q)) contains.push(p);
      if (starts.length >= 8) break;
    }
    const out = [...starts, ...contains].slice(0, 8);
    // Nothing to offer if the only suggestion is what they already typed.
    return out.length === 1 && out[0].toLowerCase() === q ? [] : out;
  }, [value]);

  useEffect(() => {
    const away = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const choose = (p) => {
    onChange(p);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (!open || !matches.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % matches.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i <= 0 ? matches.length - 1 : i - 1)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(matches[active]); }
    else if (e.key === "Escape") { setOpen(false); setActive(-1); }
  };

  const show = open && matches.length > 0;

  return (
    <div ref={wrap} className="relative">
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="address-level2"
        role="combobox"
        aria-expanded={show}
        aria-autocomplete="list"
        aria-controls={id ? `${id}-places` : undefined}
        className={className}
        {...inputProps}
      />
      {show && (
        <ul
          id={id ? `${id}-places` : undefined}
          role="listbox"
          className="absolute z-30 left-0 right-0 mt-1 max-h-60 overflow-auto rounded-xl border border-line
                     bg-surface shadow-sift-md py-1"
        >
          {matches.map((p, i) => (
            <li key={p} role="option" aria-selected={i === active}>
              {/* onMouseDown, not onClick: the input's blur would close the list
                  before a click ever landed. */}
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); choose(p); }}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                  i === active ? "bg-brand-tint text-ink" : "text-ink"
                }`}
              >
                <MapPin size={14} strokeWidth={2} aria-hidden="true" className="text-secondary shrink-0" />
                {p}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
